import { useCallback, useEffect } from 'react';
import { Author } from '../../api/Author';
import { InAppNotification } from '../../api/Types';
import {
  mergeUnreadNotification,
} from './inAppNotifications.helpers';
import useInAppNotificationPlatform from './useInAppNotificationPlatform';
import useInAppNotificationRealtime from './useInAppNotificationRealtime';
import useInAppNotificationState from './useInAppNotificationState';

interface UseInAppNotificationsResult {
  notifications: InAppNotification[];
  notificationsLoading: boolean;
  unreadNotificationsCount: number;
  handleNotificationClick: (notification: InAppNotification) => void;
}

export const useInAppNotifications = (
  author: Author | null
): UseInAppNotificationsResult => {
  const {
    notifications,
    notificationsLoading,
    unreadNotificationsCount,
    setNotifications,
    setUnreadNotificationsCount,
    resetNotificationState,
    loadInAppNotifications,
    handleNotificationClick,
  } = useInAppNotificationState(author?.id);
  const { showSystemNotification } = useInAppNotificationPlatform({
    authorId: author?.id,
    unreadNotificationsCount,
  });

  const handleUnreadRealtimeNotification = useCallback((incoming: InAppNotification) => {
    setNotifications((previous) => mergeUnreadNotification(previous, incoming, 20));
    setUnreadNotificationsCount((previous) => previous + 1);
    void showSystemNotification(incoming);
  }, [setNotifications, setUnreadNotificationsCount, showSystemNotification]);

  useInAppNotificationRealtime({
    authorId: author?.id,
    loadInAppNotifications,
    onUnreadNotification: handleUnreadRealtimeNotification,
  });

  useEffect(() => {
    if (!author?.id) {
      resetNotificationState();
      return;
    }

    void loadInAppNotifications();
  }, [author?.id, loadInAppNotifications, resetNotificationState]);

  return {
    notifications,
    notificationsLoading,
    unreadNotificationsCount,
    handleNotificationClick,
  };
};

export default useInAppNotifications;
